import { ref } from '@vue/composition-api';

/**
 * Centralized error handling composable
 * @param {Object} options - Configuration options
 * @returns {Object} Error handling utilities
 */
export function useErrorHandler(options = {}) {
  const {
    showNotification = true,
    logToConsole = true,
    reportToService = false,
    serviceName = 'ErrorLogger',
  } = options;

  const lastError = ref(null);
  const errorHistory = ref([]);
  const maxHistorySize = 10;

  /**
   * Error severity levels
   */
  const ErrorSeverity = {
    INFO: 'info',
    WARNING: 'warning',
    ERROR: 'error',
    CRITICAL: 'critical',
  };

  /**
   * Parse error object
   */
  const parseError = (error, context = '') => {
    let message = 'Nastala neočakávaná chyba';
    let severity = ErrorSeverity.ERROR;
    let details = null;

    if (typeof error === 'string') {
      message = error;
    } else if (error?.response) {
      // Axios error
      const status = error.response.status;
      details = {
        status,
        data: error.response.data,
        headers: error.response.headers,
      };

      if (status === 400) {
        message = error.response.data?.message || 'Neplatné údaje';
        severity = ErrorSeverity.WARNING;
      } else if (status === 401) {
        message = 'Nie ste prihlásený';
        severity = ErrorSeverity.WARNING;
      } else if (status === 403) {
        message = 'Nemáte oprávnenie na túto akciu';
        severity = ErrorSeverity.WARNING;
      } else if (status === 404) {
        message = 'Požadované dáta neboli nájdené';
        severity = ErrorSeverity.WARNING;
      } else if (status === 422) {
        message = error.response.data?.message || 'Validácia zlyhala';
        severity = ErrorSeverity.WARNING;
      } else if (status >= 500) {
        message = 'Chyba servera. Skúste to prosím neskôr.';
        severity = ErrorSeverity.CRITICAL;
      }
    } else if (error?.message) {
      message = error.message;
      details = {
        name: error.name,
        stack: error.stack,
      };
    }

    return {
      message,
      severity,
      details,
      context,
      timestamp: new Date().toISOString(),
      originalError: error,
    };
  };

  /**
   * Handle error
   */
  const handleError = (error, context = '') => {
    const parsedError = parseError(error, context);
    
    lastError.value = parsedError;
    
    // Add to history
    errorHistory.value.unshift(parsedError);
    if (errorHistory.value.length > maxHistorySize) {
      errorHistory.value.pop();
    }

    // Log to console (disabled for production)
    // if (logToConsole) {
    //   const logMethod = parsedError.severity === ErrorSeverity.CRITICAL ? 'error' : 'warn';
    //   console[logMethod](
    //     `[${parsedError.severity.toUpperCase()}] ${context ? `${context}: ` : ''}${parsedError.message}`,
    //     parsedError.details
    //   );
    // }

    // Report to error tracking service
    if (reportToService && window.Sentry) {
      window.Sentry.captureException(error, {
        tags: {
          context,
          severity: parsedError.severity,
        },
        extra: parsedError.details,
      });
    }

    return parsedError;
  };

  /**
   * Handle API error with retry logic
   */
  const handleApiError = async (apiCall, options = {}) => {
    const {
      maxRetries = 0,
      retryDelay = 1000,
      onRetry = null,
      context = 'API Call',
    } = options;

    let lastError = null;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await apiCall();
      } catch (error) {
        lastError = error;
        
        if (attempt < maxRetries) {
          if (onRetry) {
            onRetry(attempt + 1, maxRetries);
          }
          
          await new Promise(resolve => setTimeout(resolve, retryDelay));
        }
      }
    }

    // All retries failed
    return handleError(lastError, context);
  };

  /**
   * Clear error history
   */
  const clearErrors = () => {
    lastError.value = null;
    errorHistory.value = [];
  };

  /**
   * Get user-friendly error message
   */
  const getUserMessage = (error) => {
    const parsed = typeof error === 'string' 
      ? { message: error, severity: ErrorSeverity.INFO }
      : parseError(error);
    
    return parsed.message;
  };

  /**
   * Check if error is recoverable
   */
  const isRecoverable = (error) => {
    const parsed = parseError(error);
    return parsed.severity !== ErrorSeverity.CRITICAL;
  };

  /**
   * Get error notification color
   */
  const getNotificationColor = (severity) => {
    switch (severity) {
      case ErrorSeverity.INFO:
        return 'info';
      case ErrorSeverity.WARNING:
        return 'warning';
      case ErrorSeverity.ERROR:
        return 'error';
      case ErrorSeverity.CRITICAL:
        return 'error';
      default:
        return 'error';
    }
  };

  return {
    lastError,
    errorHistory,
    ErrorSeverity,
    handleError,
    handleApiError,
    clearErrors,
    getUserMessage,
    isRecoverable,
    getNotificationColor,
  };
}

/**
 * Global error handler setup for Vue 2
 */
export function setupGlobalErrorHandler(options = {}) {
  const {
    vue: Vue,
    router,
    store,
    onError,
    ...errorHandlerOptions
  } = options;

  const { handleError } = useErrorHandler(errorHandlerOptions);

  // Custom error callback
  const errorCallback = (error, context) => {
    handleError(error, context);
    if (onError) {
      onError(error, context);
    }
  };

  // Vue 2 error handler
  if (Vue) {
    Vue.config.errorHandler = (err, vm, info) => {
      errorCallback(err, `Vue Error (${info})`);
    };

    // Vue warning handler (development)
    // Removed for production build
  }

  // Router error handlers
  if (router) {
    router.onError((error) => {
      errorCallback(error, 'Router Error');
    });
  }

  // Global unhandled promise rejection
  window.addEventListener('unhandledrejection', (event) => {
    errorCallback(event.reason, 'Unhandled Promise Rejection');
    event.preventDefault();
  });

  // Global error event
  window.addEventListener('error', (event) => {
    errorCallback(event.error || event.message, 'Global Error');
  });
}
